'use client';

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import styles from './PopupShell.module.sass';

type Props = {
  open: boolean;
  onClose: () => void;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  children: React.ReactNode;
};

export default function PopupShell({
  open,
  onClose,
  closeOnBackdrop = true,
  closeOnEscape = true,
  children,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, closeOnEscape, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={wrapperRef}
      className={styles.wrapper}
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === wrapperRef.current) onClose();
      }}
    >
      <div className={styles.popup}>{children}</div>
    </div>,
    document.body,
  );
}
